import React from "react";

import { Pill } from "src/base-components/Pill";
import { Option, Section, Select } from "src/base-components/Select";
import { Skeleton } from "src/design-system/Skeleton";
import { isOlderModel } from "src/genai/ModelSelector/facets";
import { ModelItem } from "src/genai/ModelSelector/ModelItem";
import { ModelOption } from "src/genai/ModelSelector/types";

export type ModelSelectorDropdownProps = {
  /** List of available models to display */
  modelOptions: ModelOption[];
  /** Currently selected model ID */
  selectedModelId: string | undefined;
  /** Callback when a model is selected */
  onSelect: (modelId: string) => void;
  /** Whether the entire selector is disabled */
  disabled?: boolean;
  /** Whether the models are still loading */
  loading?: boolean;
  /** Placeholder text when no model is selected */
  placeholder?: string;
  /** data-loc attribute for testing */
  dataLoc?: string;
  /** Static content pinned below the scrollable options. */
  footer?: React.ReactNode;
  /**
   * Friendly label shown in the trigger when `selectedModelId` matches no
   * option (removed model / deleted BYOK credential). Falls back to the raw id.
   */
  missingModelDisplayName?: string;
  /**
   * The current selection can't be resolved to an option yet because the model
   * list is still loading (e.g. BYOK entries arriving after a reload). Render a
   * neutral skeleton in the trigger instead of an "Unavailable" pill until the
   * list settles.
   */
  isResolvingSelection?: boolean;
};

type GroupedModels = {
  current: ModelOption[];
  byok: ModelOption[];
  outdated: ModelOption[];
};

/**
 * Partition models for the sectioned dropdown: BYOK deployments, outdated
 * (superseded/deprecated) models, and everything else as "current".
 */
const groupModels = (models: ModelOption[]): GroupedModels => ({
  current: models.filter((model) => !model.byok && !isOlderModel(model)),
  byok: models.filter((model) => Boolean(model.byok)),
  outdated: models.filter((model) => !model.byok && isOlderModel(model)),
});

/** The selected-model content rendered inside the trigger button. */
const ModelTriggerContent: React.FC<{
  model?: ModelOption;
  fallbackLabel?: string;
  isResolvingSelection?: boolean;
}> = ({ model, fallbackLabel, isResolvingSelection }) => {
  // The selection can't be resolved yet because the option list is still
  // loading (e.g. BYOK entries arrive a moment after a reload). Show a neutral
  // skeleton until it settles — checked before the option lookup so that a BYOK
  // selection whose plain model_id coincidentally matches a Taktile-hosted
  // sibling doesn't briefly render the shared-key row instead of its BYOK
  // variant, and so a selection with no match doesn't flash a red "Unavailable".
  if (isResolvingSelection) {
    return <Skeleton height="h-5.5" width="w-24" />;
  }
  if (!model) {
    // The selection is genuinely unavailable (removed model, or a deleted BYOK
    // credential): runs against it will fail, so we surface a red "Unavailable"
    // tag. `fallbackLabel` is a friendly label or the raw id.
    return fallbackLabel ? (
      <span className="flex min-w-0 items-center gap-2 overflow-hidden">
        <span className="min-w-0 truncate font-inter-medium-13px text-gray-500">
          {fallbackLabel}
        </span>
        <span className="shrink-0">
          <Pill size="sm" variant="red">
            <Pill.Text>Unavailable</Pill.Text>
          </Pill>
        </span>
      </span>
    ) : (
      <span className="font-inter-normal-13px text-gray-400">Select model</span>
    );
  }

  return <ModelItem model={model} />;
};

/** A grouped row: provider mark + name + residency badges. */
const toGroupedOption = (model: ModelOption, sectionKey: string): Option => ({
  key: model.id,
  value: (
    <ModelItem
      dataLoc={`select-${model.id}`}
      dimmed={isOlderModel(model)}
      model={model}
    />
  ),
  disabled: model.disabled,
  disabledTooltip: model.disabledReason,
  sectionKey,
});

/** Build sectioned options: primary models, Your keys, and a collapsible Outdated group. */
const buildGroupedOptions = (
  modelOptions: ModelOption[],
  selectedModelId: string | undefined,
): { options: Option[]; sections: Section[] } => {
  const groups = groupModels(modelOptions);
  const sections: Section[] = [];
  const options: Option[] = [];

  if (groups.current.length > 0) {
    // Primary models render directly, with no section header.
    sections.push({ key: "current", displayValue: null });
    options.push(...groups.current.map((m) => toGroupedOption(m, "current")));
  }
  if (groups.byok.length > 0) {
    sections.push({
      key: "byok-wrap",
      displayValue: null,
      subsections: [
        {
          key: "byok",
          displayValue: `Your keys (${groups.byok.length})`,
          defaultExpanded: true,
        },
      ],
    });
    options.push(...groups.byok.map((m) => toGroupedOption(m, "byok")));
  }
  if (groups.outdated.length > 0) {
    // Expand the group when the current selection lives there, so it stays visible.
    const selectionIsOutdated = groups.outdated.some(
      (m) => m.id === selectedModelId,
    );
    sections.push({
      key: "outdated-wrap",
      displayValue: null,
      subsections: [
        {
          key: "outdated",
          displayValue: `Outdated models (${groups.outdated.length})`,
          defaultExpanded: selectionIsOutdated,
        },
      ],
    });
    options.push(...groups.outdated.map((m) => toGroupedOption(m, "outdated")));
  }

  return { options, sections };
};

/**
 * Model selector dropdown: models grouped into sections with residency badges,
 * built on the `Select` base component. Used with different model sources
 * (aiNode, agents) via the {@link ModelOption} view-model.
 */
export const ModelSelectorDropdown: React.FC<ModelSelectorDropdownProps> = ({
  modelOptions,
  disabled = false,
  onSelect,
  selectedModelId,
  loading = false,
  placeholder = "Select model",
  dataLoc = "model-selector",
  footer,
  missingModelDisplayName,
  isResolvingSelection = false,
}) => {
  const selectedModel = modelOptions.find((m) => m.id === selectedModelId);
  const { options, sections } = buildGroupedOptions(
    modelOptions,
    selectedModelId,
  );

  const handleChange = (value: string | null) => {
    if (value) {
      onSelect(value);
    }
  };

  return (
    <div className="w-full">
      <Select
        buttonRenderer={() => (
          <ModelTriggerContent
            fallbackLabel={missingModelDisplayName ?? selectedModelId}
            isResolvingSelection={isResolvingSelection}
            model={selectedModel}
          />
        )}
        dataLoc={dataLoc}
        disabled={disabled}
        footer={footer}
        loading={loading}
        options={options}
        placeholder={placeholder}
        searchable={false}
        sections={sections}
        value={selectedModelId}
        fullWidth
        // The options panel renders in a portal (outside the node editor's
        // lockable-focus region). Mark it as part of that region so opening the
        // dropdown doesn't drop lockable focus — otherwise the node editor flips
        // to reactive mode ~1s later and resets the form from `node.data`, wiping
        // a just-picked BYOK `credential_id` before it persists. Inert outside
        // the node editor (e.g. the agents version page has no lock tracker).
        useLockNodeEditorClassName
        onChange={handleChange}
      />
    </div>
  );
};
